/**
 * DocumentsPanel — browse / upload / AI-search for the interim
 * document-storage feature (see ../../../docs is in the backend repo;
 * design doc: mission-control/docs/document-storage-interim-design.md).
 *
 * Thin renderer over `useDocuments` (which wraps the BFF client). The BFF
 * is the authorization boundary: every action here forwards the ZITADEL
 * Bearer token and the BFF enforces the role→tier matrix. The `can()`
 * gates below only hide controls the user could never use anyway — a
 * cosmetic nicety, not a security check (a hand-crafted request still hits
 * the BFF's 403).
 *
 * Layout:
 *   - tier tabs (only the tiers the BFF says the caller can read)
 *   - an AI-search box (fans out across the caller's readable collections)
 *   - the active tier's file list with download, and (role-gated) upload +
 *     delete controls.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  FileText,
  Loader2,
  Search,
  Trash2,
  Upload as UploadIcon,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/hooks/use-auth";
import { can } from "@/lib/auth/can";
import { useDocuments } from "@/stores/useDocuments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isMultiClientEnabled } from "@/env";
import { SharePopover } from "@/features/sharing/SharePopover";
import { useSharing } from "@/features/sharing/use-sharing";
import { DocumentPreviewDialog } from "@/features/documents/DocumentPreviewDialog";
import type { SearchHit } from "@/lib/docs-api";

/** Tiers whose files are shareable with clients (the central pool). */
const SHARED_POOL_TIERS = new Set(["library", "restricted"]);

/** Human-readable byte size. */
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

/** Trigger a browser download for fetched bytes. */
function saveBlobAs(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function DocumentsPanel() {
  const { user } = useAuth();
  const {
    tiers,
    activeTier,
    blobs,
    tiersStatus,
    blobsStatus,
    mutating,
    error,
    searchResults,
    collectionsSearched,
    searchStatus,
    loadTiers,
    selectTier,
    upload,
    remove,
    download,
    search,
    clearSearch,
  } = useDocuments();

  const fileInput = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [docPreview, setDocPreview] = useState<SearchHit | null>(null);

  useEffect(() => {
    void loadTiers();
  }, [loadTiers]);

  // The active tier's permitted actions drive which controls render. Falls
  // back to read-only if the tier isn't in the (BFF-supplied) list.
  const activeActions = useMemo(
    () => tiers.find((t) => t.tier === activeTier)?.actions ?? [],
    [tiers, activeTier],
  );
  const canUploadHere = can(user, "upload:files") && activeActions.includes("write");
  const canDeleteHere = can(user, "delete:files") && activeActions.includes("delete");
  // Multi-client sharing controls: facilitator-only, pool tiers only.
  const canShareHere =
    isMultiClientEnabled() &&
    can(user, "manage:sharing") &&
    activeTier != null &&
    SHARED_POOL_TIERS.has(activeTier);
  const sharing = useSharing(canShareHere);

  const onPickFile = () => fileInput.current?.click();

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file fires change
    if (!file) return;
    const ok = await upload(file.name, file);
    if (ok) toast.success(`Uploaded ${file.name}`);
    else toast.error(useDocuments.getState().error ?? "Upload failed");
  };

  const onDownload = async (path: string) => {
    try {
      const blob = await download(path);
      saveBlobAs(blob, path.split("/").pop() ?? path);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const onDelete = async (path: string) => {
    const ok = await remove(path);
    if (ok) toast.success("Deleted");
    else toast.error(useDocuments.getState().error ?? "Delete failed");
  };

  const onSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await search(query);
  };

  if (tiersStatus === "loading" && tiers.length === 0) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading document library…
      </div>
    );
  }

  if (tiersStatus === "error" && tiers.length === 0) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-sm text-destructive">
          Couldn’t reach the document service{error ? `: ${error}` : "."}
        </p>
        <Button variant="outline" size="sm" onClick={() => void loadTiers()}>
          Retry
        </Button>
      </div>
    );
  }

  if (tiers.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don’t have access to any document tiers.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Tier tabs */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Document tiers">
        {tiers.map((t) => (
          <button
            key={t.tier}
            role="tab"
            aria-selected={t.tier === activeTier}
            onClick={() => void selectTier(t.tier)}
            className={`rounded-full border px-3 py-1 text-sm capitalize transition-colors ${
              t.tier === activeTier
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.tier}
          </button>
        ))}
      </div>

      {/* AI search */}
      <form onSubmit={onSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by filename or content across documents you can access…"
            className="pl-8"
            aria-label="Document search by filename or content"
          />
        </div>
        <Button type="submit" disabled={searchStatus === "loading" || !query.trim()}>
          {searchStatus === "loading" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            "Search"
          )}
        </Button>
        {searchResults.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              clearSearch();
            }}
          >
            <X className="size-4" />
          </Button>
        )}
      </form>

      {/* Search results (when present, they replace the browse list) */}
      {searchResults.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {searchResults.length} result{searchResults.length === 1 ? "" : "s"} across{" "}
            {collectionsSearched.join(", ") || "no collections"}
          </p>
          {searchResults.map((hit, i) => (
            <button
              key={`${hit.filename}-${i}`}
              type="button"
              onClick={() => setDocPreview(hit)}
              className="w-full rounded-md border border-border p-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileText className="size-4 text-muted-foreground" />
                {hit.filename}
                {hit.page ? (
                  <span className="text-xs text-muted-foreground">· p{hit.page}</span>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {hit.score.toFixed(3)}
                </span>
              </div>
              <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{hit.content}</p>
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* Browse toolbar */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {blobsStatus === "loading" ? "Loading…" : `${blobs.length} file(s)`}
            </span>
            <div className="ml-auto flex gap-2">
              {canUploadHere && (
                <>
                  <input
                    ref={fileInput}
                    type="file"
                    className="hidden"
                    onChange={(e) => void onFileChosen(e)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onPickFile}
                    disabled={mutating}
                  >
                    {mutating ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <UploadIcon className="size-4" />
                    )}
                    Upload
                  </Button>
                </>
              )}
            </div>
          </div>

          {error && blobsStatus === "error" && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          {/* File list */}
          <ul className="divide-y divide-border rounded-md border border-border">
            {blobs.length === 0 && blobsStatus !== "loading" && (
              <li className="p-4 text-sm text-muted-foreground">No documents in this tier yet.</li>
            )}
            {blobs.map((b) => (
              <li key={b.path} className="flex items-center gap-3 p-3">
                <FileText className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{b.path}</p>
                  <p className="text-xs text-muted-foreground">
                    {humanSize(b.size)}
                    {b.last_modified
                      ? ` · ${new Date(b.last_modified).toLocaleString()}`
                      : ""}
                  </p>
                </div>
                {canShareHere && activeTier && (
                  <SharePopover tier={activeTier} path={b.path} sharing={sharing} />
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Download ${b.path}`}
                  onClick={() => void onDownload(b.path)}
                >
                  <Download className="size-4" />
                </Button>
                {canDeleteHere && (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Delete ${b.path}`}
                    disabled={mutating}
                    onClick={() => void onDelete(b.path)}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {docPreview && (
        <DocumentPreviewDialog hit={docPreview} onClose={() => setDocPreview(null)} />
      )}
    </div>
  );
}
